import { useFormContext } from "../../../context/FormContext";
import { useState } from "react";
import { generateObjectId } from "../../../utils/objectIdUtils";
import Button from "../../../components/FormComponents/Button";
import Input from "../../../components/FormComponents/Input";
import CodeEditor from "../../../components/BuilderComponents/CodeEditor/CodeEditor";
import ButtonActionIcon from "../../../components/BuilderComponents/ButtonActionIcon";
import BuilderRightSideBar from "../../../Layouts/BuilderRightSideBar";
import CustomJS from "../../../components/BuilderComponents/CustomJS";

const OnChangeCustomJs = ({ from = "form" }) => {
  const { formActvityData, setFormActivityData, selectedForm, formSectionsData } = useFormContext();

  const [editingScript, setEditingScript] = useState(null);
  const [newScript, setNewScript] = useState({ name: "", script: "" });
  const [showSidebar, setShowSidebar] = useState(false);

  // --- Helpers ---
  const getScripts = () => {
    if (from === "form") {
      return formActvityData?.onchange_custom_js?.scripts || [];
    }
    if (from === "subform") {
      return formSectionsData.current?.[0]?.onchange_custom_js?.scripts || [];
    }
    return [];
  };

  const updateScripts = (scripts) => {
    if (from === "form") {
      setFormActivityData((prev) => ({
        ...prev,
        onchange_custom_js: { scripts }
      }));
    } else if (from === "subform") {
      // mutate ref safely
      if (formSectionsData.current?.[0]) {
        formSectionsData.current[0] = {
          ...formSectionsData.current[0],
          onchange_custom_js: { scripts }
        };
      }
    }
  };

  // --- CRUD ---
  const addScript = () => {
    if (!newScript.name.trim() || !newScript.script.trim()) {
      return;
    }

    const newScriptWithId = {
      id: generateObjectId(),
      name: newScript.name.trim(),
      script: newScript.script.trim(),
      description: newScript.description?.trim() || ""
    };

    updateScripts([...getScripts(), newScriptWithId]);
    setNewScript({ name: "", script: "" });
    setShowSidebar(false);
  };

  const deleteScript = (scriptId) => {
    updateScripts(getScripts().filter((s) => s.id !== scriptId));
  };

  const startEditing = (script) => {
    setEditingScript({ ...script });
    setShowSidebar(true);
  };

  const saveEdit = () => {
    if (!editingScript.name.trim() || !editingScript.script.trim()) {
      return;
    }

    const updatedScripts = getScripts().map((s) =>
      s.id === editingScript.id
        ? {
            ...editingScript,
            name: editingScript.name.trim(),
            script: editingScript.script.trim(),
            description: editingScript.description?.trim() || ""
          }
        : s
    );

    updateScripts(updatedScripts);
    setEditingScript(null);
    setShowSidebar(false);
  };

  const cancelEdit = () => {
    setEditingScript(null);
    setNewScript({ name: "", script: "" });
    setShowSidebar(false);
  };

  // --- UI ---
  const scripts = getScripts();

  const emptyStateStyle = {
    textAlign: "center",
    color: "#6c757d",
    fontSize: "12px",
    padding: "40px 20px",
    height: "100%"
  };

  const tableID = selectedForm?.parentData?.split("/")[0]; // ✅ pass to CustomJS

  return (
    <div className="p_2 flex_column gap_2 mx_auto" style={{ maxWidth: "1200px", width: "1000px" }}>
      <Button
        onClick={() => {
          setEditingScript(null);
          setNewScript({ name: "", script: "" });
          setShowSidebar(true);
        }}
        variant="primary"
        size="medium"
        className="ml_auto"
      >
        Add New Script
      </Button>

      {/* Empty state */}
      {scripts.length === 0 ? (
        <div className="d_flex align_center justify_center" style={{ height: "100%", minHeight: "60vh" }}>
          <div style={emptyStateStyle}>No custom JS scripts added yet. Click "Add New Script" to get started.</div>
        </div>
      ) : (
        scripts.map((script) => (
          <div
            key={script.id}
            style={{
              backgroundColor: "#ffffff",
              border: "1px solid #dee2e6",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "15px"
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "10px"
              }}
            >
              <h3 style={{ fontSize: "18px", fontWeight: "bold", color: "#333" }}>{script.name}</h3>
              <div style={{ display: "flex", gap: "8px" }}>
                <ButtonActionIcon type="edit" size="sm" title="Edit Script" onClick={() => startEditing(script)} />
                <ButtonActionIcon
                  type="delete"
                  size="sm"
                  title="Delete Script"
                  onClick={() => deleteScript(script.id)}
                />
              </div>
            </div>
            {script.description && (
              <p style={{ fontSize: "14px", color: "#555", marginBottom: "10px" }}>{script.description}</p>
            )}
            <CodeEditor value={script.script} language="javascript" height="200px" readOnly />
          </div>
        ))
      )}

      {/* Sidebar for Add/Edit */}
      <BuilderRightSideBar
        isOpen={showSidebar}
        onClose={setShowSidebar}
        heading={editingScript ? "Edit Script" : "Add New Script"}
        size="medium"
      >
        <div className="p_3">
          <Input
            type="text"
            label="Script Name"
            placeholder="Enter script name"
            value={editingScript ? editingScript.name : newScript.name}
            onChange={(e) =>
              editingScript
                ? setEditingScript((prev) => ({ ...prev, name: e.target.value }))
                : setNewScript((prev) => ({ ...prev, name: e.target.value }))
            }
            containerClassName="mb_3"
          />

          <Input
            type="text"
            label="Description"
            placeholder="Enter script description"
            value={editingScript ? editingScript.description : newScript.description}
            onChange={(e) =>
              editingScript
                ? setEditingScript((prev) => ({ ...prev, description: e.target.value }))
                : setNewScript((prev) => ({ ...prev, description: e.target.value }))
            }
            containerClassName="mb_3"
          />

          <div className="mb_3 flex_column gap_1">
            <CustomJS
              inputString={editingScript ? editingScript.script : newScript.script}
              handleChange={(val) =>
                editingScript
                  ? setEditingScript((prev) => ({ ...prev, script: val }))
                  : setNewScript((prev) => ({ ...prev, script: val }))
              }
              tableID={tableID}
              label="Custom JS"
              height="300px"
            />
          </div>

          <div className="d_flex gap_2">
            <Button onClick={editingScript ? saveEdit : addScript} variant="primary">
              {editingScript ? "Save Changes" : "Add Script"}
            </Button>
            <Button onClick={cancelEdit} variant="secondary">
              Cancel
            </Button>
          </div>
        </div>
      </BuilderRightSideBar>
    </div>
  );
};

export default OnChangeCustomJs;
